import { FC, useMemo } from 'react';

import { ColumnTable, Entry } from '@/components/ColumnTable';
import { RelationName } from '@/components/RelationName';
import { useSeed } from '@/hooks/dbtStrategy/useSeed';
import { useCommonLineageEntries } from '@/hooks/useCommonLineageEntries';
import { ResourceDetailsParams } from './panelDetailTypes';
import { TagLinks } from './TagLinks';

export const useSeedDetailsPanel = ({
  uniqueId,
  projectName,
}: ResourceDetailsParams) => {
  const seedQuery = useSeed(uniqueId);

  const commonEntries = useCommonLineageEntries(projectName);

  return useMemo(() => {
    const seed = seedQuery.data.applied;

    if (!seed) {
      return { ...seedQuery, rows: null, description: null };
    }

    const rows: Entry[] = [
      ...commonEntries,
      {
        key: 'Relation',
        data: <RelationName relation={seed} />,
        disableTooltip: true,
      },
      {
        key: 'Tags',
        data: <TagLinks tags={seed.tags} />,
      },
      { key: 'Package', data: uniqueId.packageName },
    ];

    return { ...seedQuery, rows, description: seed.description };
  }, [seedQuery, commonEntries, uniqueId.packageName]);
};

export const SeedDetailsTable: FC<ResourceDetailsParams> = ({
  className,
  children,
  compact,
  ...props
}) => {
  const seedDetails = useSeedDetailsPanel(props);

  return (
    <div className={className}>
      {children?.(seedDetails)}
      <ColumnTable
        tableEntries={seedDetails.rows}
        compact={compact}
        isLoading={seedDetails.fetching}
      />
    </div>
  );
};
